import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import type { NotificationItem } from '@/lib/api/notifications';

import { resolveNotificationCopy } from './notification-copy';
import { resolveFeedState } from './notification-feed-state';
import { resolveNotificationHref } from './notification-href';

const NOTIFICATIONS_NAMESPACE = 'notifications';

interface NotificationItemRowProps {
  notification: NotificationItem;
  orgSlug: string;
  onNavigate: () => void;
}

function NotificationItemRow({ notification, orgSlug, onNavigate }: NotificationItemRowProps) {
  const { t, i18n } = useTranslation(NOTIFICATIONS_NAMESPACE);

  const isUnread = notification.readAt === null;
  const href = resolveNotificationHref(notification, orgSlug);
  const rowClassName = cn('flex gap-3 px-4 py-3', isUnread && 'bg-accent/40');

  const { title, body } = resolveNotificationCopy(notification, t, (key) =>
    i18n.exists(key, { ns: NOTIFICATIONS_NAMESPACE }),
  );

  const content = (
    <>
      {notification.kind === 'alert' ? (
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600 dark:text-yellow-400" />
      ) : (
        <Info className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
      )}
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium">{title}</p>
          {isUnread ? (
            <>
              <span className="bg-primary size-1.5 shrink-0 rounded-full" aria-hidden />
              <span className="sr-only">{t('panel.unread')}</span>
            </>
          ) : null}
        </div>
        <p className="text-muted-foreground text-sm">{body}</p>
        <p className="text-muted-foreground text-xs">
          {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
        </p>
      </div>
    </>
  );

  if (href === null) {
    return <div className={rowClassName}>{content}</div>;
  }

  return (
    <Link
      to={href}
      onClick={onNavigate}
      className={cn(rowClassName, 'hover:bg-accent focus-visible:bg-accent transition-colors outline-none')}
    >
      {content}
    </Link>
  );
}

interface NotificationFeedProps {
  notifications: NotificationItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  orgSlug: string;
  onNavigate: () => void;
}

export function NotificationFeed({ notifications, isLoading, isError, orgSlug, onNavigate }: NotificationFeedProps) {
  const { t } = useTranslation(NOTIFICATIONS_NAMESPACE);
  const feedState = resolveFeedState(notifications, isLoading, isError);
  const items = notifications ?? [];

  return (
    <div className="flex flex-col">
      <div className="border-b px-4 py-3">
        <p className="text-sm font-semibold">{t('panel.title')}</p>
      </div>
      <div className="max-h-[360px] overflow-y-auto overscroll-contain">
        <div className="flex flex-col divide-y">
          {feedState === 'loading' ? (
            <div className="text-muted-foreground px-4 py-6 text-center text-sm">{t('panel.loading')}</div>
          ) : feedState === 'error' ? (
            <div className="text-muted-foreground px-4 py-6 text-center text-sm">{t('panel.error')}</div>
          ) : feedState === 'empty' ? (
            <div className="text-muted-foreground px-4 py-6 text-center text-sm">{t('panel.empty')}</div>
          ) : (
            items.map((notification) => (
              <NotificationItemRow
                key={notification.id}
                notification={notification}
                orgSlug={orgSlug}
                onNavigate={onNavigate}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
